import DialogController from "./dialog_controller"

// The reply input lives outside the frame. Only the operation preview and history
// can be refreshed; closing a preview never visits or replaces the conversation.
export default class extends DialogController {
  static targets = ["frame", "timeline", "menu", "closeButton", "busy"]

  connect() {
    super.connect()
    this.observer = new ResizeObserver(() => this.resizeMenu())
    this.observer.observe(this.element.closest(".op-line-workspace"))
    this.resizeMenu()
  }

  resizeMenu() {
    if (!this.hasMenuTarget) return
    const wide = this.element.closest(".op-line-workspace").clientWidth >= 760
    if (wide === this.wide) return
    this.wide = wide
    this.menuTarget.open = wide
  }

  open(event) {
    event.preventDefault()
    if (this.submission || this.dialog.open) return
    this.timelinePosition = this.timelineTarget.scrollTop
    this.wasAtBottom = this.timelineTarget.scrollHeight - this.timelineTarget.clientHeight - this.timelinePosition < 64
    this.frameTarget.textContent = "確認内容を読み込んでいます…"
    this.frameTarget.src = event.currentTarget.href
    super.open(event)
  }

  // A form response can replace this frame after its GET was cancelled. Keep the
  // dialog open until Turbo finishes that submission, without aborting accepted work.
  submissionStarted(event) {
    this.submission = event.detail.formSubmission
    this.requestFinished = false
    this.awaitingRender = false
    this.renderComplete = false
    this.setBusy(true)
  }

  submissionEnded(event) {
    if (event.detail.formSubmission !== this.submission) return
    this.requestFinished = true
    this.finishSubmission()
  }

  responseReceived(event) {
    if (!this.submission || event.target !== this.submission.formElement) return
    // Turbo's submit-end may precede the asynchronous frame/stream render.
    this.awaitingRender = /^(text\/html|text\/vnd\.turbo-stream\.html)/.test(event.detail.fetchResponse.contentType || "")
  }

  frameLoaded(event) {
    if (event.target === this.frameTarget) this.markRendered(this.submission)
  }

  streamRendering(event) {
    if (!this.submission || event.target.target !== this.frameTarget.id) return
    const submission = this.submission
    const render = event.detail.render
    event.detail.render = async (stream) => {
      try { await render(stream) }
      finally { this.markRendered(submission) }
    }
  }

  markRendered(submission) {
    if (!submission || submission !== this.submission) return
    this.renderComplete = true
    this.finishSubmission()
  }

  finishSubmission() {
    if (!this.requestFinished || (this.awaitingRender && !this.renderComplete)) return
    this.submission = null
    this.setBusy(false)
  }

  guardSubmit(event) {
    if (!this.submission) return
    event.preventDefault()
    event.stopImmediatePropagation()
  }

  cancel(event) {
    if (this.submission) event.preventDefault()
  }

  close() {
    if (!this.submission) super.close()
  }

  setBusy(busy) {
    this.closeButtonTarget.disabled = busy
    this.busyTarget.hidden = !busy
  }

  discard() {
    this.submission = null
    this.setBusy(false)
    super.discard()
  }

  closed() {
    super.closed()
    if (this.dialog.open) return
    // A stale request must not reappear when another menu item is opened.
    this.frameTarget.removeAttribute("src")
    this.frameTarget.replaceChildren()
    this.timelineTarget.scrollTop = this.wasAtBottom ? this.timelineTarget.scrollHeight : (this.timelinePosition ?? this.timelineTarget.scrollTop)
  }

  missing(event) {
    this.markRendered(this.submission)
    event.preventDefault()
    this.frameTarget.textContent = "確認画面を開けませんでした。ログイン状態を確認してから、閉じて開き直してください。"
  }

  disconnect() {
    this.observer?.disconnect()
    super.disconnect()
  }
}
